import { useState } from "react";
import type { Deal, Gate, Overlay, TriState } from "../types";
import { calc } from "../engine/engine";
import { eligibility, resolveTract } from "../lib/eligibility";
import { PROVIDERS, providerStatus, fetchAreaIntel, type AreaIntel, type ProviderStatus } from "../lib/providers";
import { enrichDeal, type DealEnrichment } from "../lib/enrich";
import { analyzeDeal } from "../lib/analyzer";
import { emailMemo } from "../lib/email";
import { syncDealToGhl } from "../lib/ghl";
import { isCloud } from "../lib/supabase";
import { money, money0, pct, x2 } from "../lib/format";
import { Term } from "./Tooltip";
import { DealInputs } from "./DealInputs";
import { Projections } from "./Projections";
import { RehabPlan } from "./RehabPlan";
import { Diligence } from "./Diligence";
import { Negotiation } from "./Negotiation";
import { Upsell } from "./Upsell";
import { hasFeature } from "../lib/plan";
import { DevelopmentFeasibility } from "./DevelopmentFeasibility";

function Ring({ v, band, gated }: { v: number; band: string; gated: boolean }) {
  const off = 314 - (314 * (gated ? 0 : v)) / 100;
  return (
    <svg className="ring" width="116" height="116" viewBox="0 0 120 120">
      <circle cx="60" cy="60" r="50" fill="none" stroke="#EBE5D6" strokeWidth="10" />
      <circle cx="60" cy="60" r="50" fill="none" stroke="#B08948" strokeWidth="10" strokeLinecap="round" strokeDasharray="314" strokeDashoffset={off} transform="rotate(-90 60 60)" />
      <text x="60" y="57" textAnchor="middle" className="num">{gated ? "—" : v}</text>
      <text x="60" y="74" textAnchor="middle" className="lab">{band}</text>
    </svg>
  );
}

function Pe({ children }: { children: React.ReactNode }) {
  return (
    <div className="pe">
      <div className="l">◆ In plain English</div>
      <p>{children}</p>
    </div>
  );
}

const tagClass = (cls: string) => "tag t-" + (cls === "pass" ? "pass" : cls === "watch" ? "watch" : "fail");

const SECTIONS: [string, string][] = [
  ["overview", "Overview"],
  ["inputs", "Inputs"],
  ["rent", "Section 8 Rent"],
  ["elig", "Eligibility"],
  ["data", "Data Sources"],
  ["gates", "Gates"],
  ["fin", "Financials"],
  ["proj", "Projections"],
  ["dd", "Due Diligence"],
  ["nego", "Negotiation"],
  ["rehab", "Rehab & Team"],
  ["ins", "Insurance"],
  ["cap", "Capital"],
  ["takeout", "Takeout Risk"],
  ["inv", "Investors"],
  ["tax", "Tax"],
  ["legal", "Legal"],
  ["score", "Score"],
  ["report", "Report"],
];

export function DealWorkspace({
  deal,
  onChange,
  onDelete,
}: {
  deal: Deal;
  onChange: (patch: Partial<Deal>) => void;
  onDelete: () => void;
}) {
  if (deal.kind === "land-development" && deal.development) {
    return <DevelopmentFeasibility deal={deal} onChange={onChange} onDelete={onDelete} />;
  }
  const m = calc(deal);
  const s = m.score;

  return (
    <>
      <nav className="subnav noprint">
        {SECTIONS.map(([id, label]) => (
          <a key={id} href={"#sec-" + id}>{label}</a>
        ))}
      </nav>

      <div className="wrap">
        {/* OVERVIEW */}
        <h2 className="sechead" id="sec-overview">Overview</h2>
        <div className="grid deal-two-col" style={{ gridTemplateColumns: "auto 1fr", alignItems: "center" }}>
          <div className="card pad" style={{ textAlign: "center" }}><Ring v={s.composite} band={s.band} gated={s.gated} /></div>
          <div className="grid g4">
            <Kpi label={<Term>Cap rate</Term>} value={pct(m.capOnCost)} sub="on all-in cost" />
            <Kpi label={<Term>DSCR</Term>} value={x2(m.dscr)} sub={"min " + x2(deal.dscrMin)} good={m.dscr >= deal.dscrMin} />
            <Kpi label={<Term>Cash-on-cash</Term>} value={pct(m.coc)} sub="post-refi" />
            <Kpi label={<Term k="IRR">IRR</Term>} value={m.projIRR ? pct(m.projIRR) : "—"} sub="5-yr project" />
            <Kpi label="All-in cost" value={money0(m.allIn)} sub="purchase+rehab+soft" />
            <Kpi label={<Term>Stabilized value</Term>} value={money0(m.stabVal)} sub={money(m.stabVal - m.allIn) + " vs cost"} good={m.stabVal > m.allIn} />
            <Kpi label={<Term>Debt yield</Term>} value={pct(m.debtYield)} sub="lender cushion" />
            <Kpi label="Equity multiple" value={x2(m.equityMult)} sub="5-yr" />
          </div>
        </div>
        <Pe>
          {s.gated
            ? "This deal is stopped by a failed compliance gate — see Gates below. No score until it clears."
            : "The single score summarizes everything below. Open Inputs to change any assumption and watch it all recompute."}
        </Pe>

        {/* INPUTS */}
        <h2 className="sechead" id="sec-inputs">Inputs — change anything, everything recomputes</h2>
        <DealInputs deal={deal} onChange={onChange} onDelete={onDelete} />

        {/* RENT */}
        <h2 className="sechead" id="sec-rent">Section 8 rent</h2>
        <Pe>
          You charge what the housing authority approves, not "market rent." It's built from HUD's <Term k="FMR / SAFMR">FMR / SAFMR</Term> ceiling minus a utility allowance; the government pays most via the <Term>HAP</Term> check.
        </Pe>
        <div className="grid g2">
          <div className="card pad">
            <div className="eyebrow">2BR rent build-up</div>
            <table className="t" style={{ marginTop: 6 }}>
              <tbody>
                <Row l={<Term k="FMR / SAFMR">SAFMR (2BR)</Term>} v={money0(deal.safmr2)} />
                <Row l={<Term>Payment standard</Term>} v={money0(deal.safmr2 * deal.paymentStd)} />
                <Row l="Target contract rent" v={money0(deal.u2r)} />
                <Row l="Utility allowance" v={"−" + money0(deal.ua)} />
                <Row l="Net to owner (2BR)" v={money0(deal.u2r - deal.ua)} tot />
              </tbody>
            </table>
          </div>
          <div className="card pad">
            <div className="eyebrow">Gross potential rent</div>
            <table className="t" style={{ marginTop: 6 }}>
              <tbody>
                <Row l={`${deal.u2c} × 2BR @ ${money0(deal.u2r)}`} v={money0(deal.u2c * deal.u2r * 12)} />
                <Row l={`${deal.u1c} × 1BR @ ${money0(deal.u1r)}`} v={money0(deal.u1c * deal.u1r * 12)} />
                <Row l={<Term k="NOI">Gross potential rent</Term>} v={money0(m.gpr)} tot />
              </tbody>
            </table>
          </div>
        </div>

        {/* ELIGIBILITY */}
        <h2 className="sechead" id="sec-elig">Eligibility & designations</h2>
        <EligibilityPanel deal={deal} onChange={onChange} />

        {/* DATA SOURCES */}
        <h2 className="sechead" id="sec-data">Data sources — wiring the property to the world</h2>
        <DataSourcesPanel deal={deal} onChange={onChange} />

        {/* GATES */}
        <h2 className="sechead" id="sec-gates">The four compliance gates</h2>
        <Pe>
          Four hard checks. If any one <b>fails</b>, the deal is stopped until you fix it.{" "}
          {m.gates.anyFail ? "One gate is failing right now — the deal cannot proceed." : "All four are currently pass or conditional (fixable)."}
        </Pe>
        <div className="grid g2">
          <GateCard letter="A" title="Physical — NSPIRE" g={m.gates.A} />
          <GateCard letter="B" title="Rent — ceiling & reasonableness" g={m.gates.B} />
          <GateCard letter="C" title="Program — PHA & eligibility" g={m.gates.C} />
          <GateCard letter="D" title="Legal / zone / environmental" g={m.gates.D} />
        </div>

        {/* FINANCIALS */}
        <h2 className="sechead" id="sec-fin">The financials</h2>
        <div className="grid g2">
          <div className="card pad">
            <div className="eyebrow">Income → NOI</div>
            <table className="t" style={{ marginTop: 6 }}>
              <tbody>
                <Row l="Gross potential rent" v={money0(m.gpr)} />
                <Row l={`Vacancy (${pct(deal.vacPct, 0)})`} v={"−" + money0(m.vac)} />
                <Row l="Other income" v={"+" + money0(deal.otherInc)} />
                <Row l="Effective gross income" v={money0(m.egi)} tot />
                <Row l="Operating expenses" v={"−" + money0(m.opex)} />
                <Row l={<Term>NOI</Term>} v={money0(m.noi)} tot />
              </tbody>
            </table>
          </div>
          <div className="card pad">
            <div className="eyebrow">Return scorecard</div>
            <div className="tablewrap">
              <table className="t" style={{ marginTop: 6 }}>
                <thead><tr><th>Metric</th><th className="n">Deal</th><th className="n">Target</th><th>Verdict</th></tr></thead>
                <tbody>
                  <MetricRow l={<Term>Cap rate</Term>} v={pct(m.capOnCost)} t="≥ 6.5%" ok={m.capOnCost >= 0.065} />
                  <MetricRow l={<Term>DSCR</Term>} v={x2(m.dscr)} t={"≥ " + x2(deal.dscrMin)} ok={m.dscr >= deal.dscrMin} />
                  <MetricRow l={<Term>Debt yield</Term>} v={pct(m.debtYield)} t="≥ 8%" ok={m.debtYield >= 0.08} />
                  <MetricRow l={<Term>Cash-on-cash</Term>} v={pct(m.coc)} t="≥ 6%" ok={m.coc >= 0.06} />
                  <MetricRow l="Break-even occ." v={pct(m.breakEven, 0)} t="≤ 85%" ok={m.breakEven <= 0.85} />
                  <MetricRow l={<Term k="IRR">IRR (5-yr)</Term>} v={m.projIRR ? pct(m.projIRR) : "—"} t="≥ 14%" ok={(m.projIRR ?? 0) >= 0.14} />
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* PROJECTIONS */}
        <h2 className="sechead" id="sec-proj">Projections — equity, amortization &amp; the refi snowball</h2>
        <Projections deal={deal} />

        {/* DUE DILIGENCE */}
        <h2 className="sechead" id="sec-dd">Due diligence — the inspection matrix &amp; your leverage</h2>
        <Diligence deal={deal} onChange={onChange} />

        {/* NEGOTIATION */}
        <h2 className="sechead" id="sec-nego">Negotiation — findings into a data-backed offer</h2>
        <Negotiation deal={deal} />

        {/* REHAB & TEAM */}
        <h2 className="sechead" id="sec-rehab">Rehab &amp; team — operationalize the deal</h2>
        <RehabPlan deal={deal} onChange={onChange} />

        {/* INSURANCE */}
        <h2 className="sechead" id="sec-ins">Florida insurance — the make-or-break number</h2>
        <Pe>In Florida, insurance can wreck cash flow faster than interest rates. Beacon stress-tests a premium spike (<Term k="Re-quote risk">re-quote risk</Term>).</Pe>
        <div className="grid g3">
          <Kpi label="Base premium" value={money0(deal.insBase)} sub={"→ DSCR " + x2(m.dscr)} />
          <Kpi label="Hard-market premium" value={money0(deal.insStress)} sub={"→ DSCR " + x2(m.dscrStress)} good={m.dscrStress >= deal.dscrMin} />
          <Kpi label="Roof age" value={String(deal.roofAge)} sub={deal.roofAge > 15 ? ">15 — insurability risk" : "OK"} good={deal.roofAge <= 15} />
        </div>
        {m.dscrStress < deal.dscrMin ? (
          <div className="flag bad"><span>!</span><div>If insurance hits {money0(deal.insStress)}, <b>DSCR falls to {x2(m.dscrStress)}</b> — below the {x2(deal.dscrMin)} line. The deal's #1 sensitivity.</div></div>
        ) : (
          <div className="flag ok"><span>✓</span><div>Even at the stressed premium, DSCR holds at {x2(m.dscrStress)}.</div></div>
        )}

        {/* CAPITAL */}
        <h2 className="sechead" id="sec-cap">Capital stack</h2>
        <div className="grid g2">
          <div className="card pad">
            <div className="eyebrow">Phase 1 — buy & fix</div>
            <table className="t" style={{ marginTop: 6 }}>
              <tbody>
                <Row l={<Term>Bridge loan</Term>} v={money0(m.bridge)} />
                <Row l="Sponsor equity" v={money0(m.gpEquity)} />
                <Row l={<><Term>LP</Term> equity</>} v={money0(m.lpEquity)} />
                <Row l="Total sources" v={money0(m.bridge + m.equity)} tot />
                <Row l="Total uses (all-in)" v={money0(m.allIn)} />
              </tbody>
            </table>
          </div>
          <div className="card pad">
            <div className="eyebrow">Phase 2 — permanent takeout</div>
            <table className="t" style={{ marginTop: 6 }}>
              <tbody>
                <Row l={<>Perm loan (<Term>LTV</Term> {pct(m.ltv, 0)})</>} v={money0(m.permLoan)} />
                <Row l="Sized by" v={m.ltvBinds ? "LTV test" : "DSCR test"} />
                <Row l="Repays bridge" v={money0(m.bridge)} />
                <Row l={<Term k="Takeout / refinance">Cash returned to equity</Term>} v={money(m.cashReturned)} tot />
              </tbody>
            </table>
          </div>
        </div>

        {/* TAKEOUT */}
        <h2 className="sechead" id="sec-takeout">Refinance ("takeout") risk</h2>
        <Pe>The plan depends on the permanent loan being big enough to pay off the bridge. If it shrinks you may face a <Term k="Cash-in refinance">cash-in refinance</Term>. Beacon stress-tests it.</Pe>
        <div className="card pad">
          <div className="tablewrap">
            <table className="t">
              <thead><tr><th>Scenario</th><th>What changed</th><th className="n">Perm loan</th><th className="n">vs. bridge</th><th>Result</th></tr></thead>
              <tbody>
                {m.takeout.map((t) => {
                  const cls = t.gap < 0 ? "t-fail" : t.gap < 8000 ? "t-watch" : "t-pass";
                  const label = t.gap < 0 ? "Cash-in" : t.gap < 8000 ? "Barely" : "Works";
                  return (
                    <tr key={t.name}>
                      <td><b>{t.name}</b></td>
                      <td style={{ color: "var(--muted)" }}>{t.note}</td>
                      <td className="n">{money0(t.loan)}</td>
                      <td className="n" style={{ color: t.gap < 0 ? "var(--fail)" : t.gap < 8000 ? "var(--brass-deep)" : "var(--pass)" }}>{money(t.gap)}</td>
                      <td><span className={"tag " + cls}>{label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {m.takeout[3].gap < 0 && (
            <div className="flag warn"><span>◐</span><div><b>Honest read:</b> in the worst case you'd need about <b>{money0(-m.takeout[3].gap)}</b> of extra equity to refinance. Disclosed up front — and why the execution score is held down.</div></div>
          )}
        </div>

        {/* INVESTORS */}
        <h2 className="sechead" id="sec-inv">Investors — how profits are shared</h2>
        <Pe>Money is paid in a set order called a <Term>Waterfall</Term>: investors get their money back and a <Term k="Preferred return">preferred return</Term> first, then profit is split with you (the <Term k="GP / Sponsor">GP</Term>).</Pe>
        <div className="grid g2">
          <div className="card pad">
            <div className="eyebrow">The waterfall</div>
            <div style={{ marginTop: 8 }}>
              <WfStep n={1}>Return of capital — LPs get their {money0(m.lpEquity)} back.</WfStep>
              <WfStep n={2}>{pct(deal.prefPct, 0)} <Term>Preferred return</Term> before the GP shares profit.</WfStep>
              <WfStep n={3}>GP catch-up to the agreed split.</WfStep>
              <WfStep n={4}>{Math.round((1 - deal.gpPromote) * 100)}/{Math.round(deal.gpPromote * 100)} split — the GP <Term k="Promote / carry">promote</Term>.</WfStep>
            </div>
          </div>
          <div className="card pad">
            <div className="eyebrow">Investors (proj. 5-yr @ {x2(m.equityMult)})</div>
            <table className="t" style={{ marginTop: 6 }}>
              <thead><tr><th>Investor</th><th className="n">Invested</th><th className="n">Proj. total</th></tr></thead>
              <tbody>
                <LpRow name="LP 1" amt={deal.lp1} mult={m.equityMult} />
                <LpRow name="LP 2" amt={deal.lp2} mult={m.equityMult} />
                <LpRow name="LP 3" amt={deal.lp3} mult={m.equityMult} />
                <Row l="Total LP" v={money0(m.lpEquity) + " → " + money0(m.lpEquity * m.equityMult)} tot />
              </tbody>
            </table>
            <div className="note">Est. <Term>IRR</Term> to LPs ≈ {m.lpIRR ? pct(m.lpIRR) : "—"} · <Term k="SPE / SPV LLC">SPE LLC</Term> holds this property only.</div>
          </div>
        </div>

        {/* TAX */}
        <h2 className="sechead" id="sec-tax">Taxes — what investors keep</h2>
        <Pe><Term k="NOI">Depreciation</Term> lets the deal show a paper loss while paying real cash; a cost-segregation study front-loads it, so investors often keep <em>more</em> after tax.</Pe>
        <div className="grid g3">
          <Kpi label="Yr-1 paper loss (est.)" value={"~" + money0(m.allIn * 0.1)} sub="via cost-seg" />
          <Kpi label="Pre-tax LP IRR" value={m.lpIRR ? pct(m.lpIRR) : "—"} sub="from projection" />
          <Kpi label="After-tax LP IRR" value={m.lpIRR ? pct(m.lpIRR + 0.018) : "—"} sub="depreciation boost" good />
        </div>
        <div className="flag ok"><span>✓</span><div>Each investor gets a <Term k="Reg D 506(b)">K-1</Term> yearly; Beacon tracks the data and hands it to the CPA. A 1031 exchange is flagged at sale.</div></div>

        {/* LEGAL */}
        <h2 className="sechead" id="sec-legal">The legal side of raising money</h2>
        <Pe>Taking investor money is a securities offering with real rules. Beacon organizes the paperwork; your attorney signs off. This is a <Term k="Reg D 506(b)">506(b)</Term> raise from people you know.</Pe>
        <div className="grid g2">
          <div className="card pad">
            <div className="eyebrow">Offering setup</div>
            <table className="t" style={{ marginTop: 6 }}>
              <tbody>
                <Row l="Structure" v="Reg D 506(b)" />
                <Row l="Entity" v="SPE LLC (this deal only)" />
                <Row l="Investors" v={String((deal.lp1 > 0 ? 1 : 0) + (deal.lp2 > 0 ? 1 : 0) + (deal.lp3 > 0 ? 1 : 0))} />
                <Row l="Public advertising" v="Not allowed" />
              </tbody>
            </table>
          </div>
          <div className="card pad">
            <div className="eyebrow">Document tracker</div>
            <table className="t" style={{ marginTop: 6 }}>
              <tbody>
                <tr><td>PPM</td><td className="n"><span className="tag t-pass">Final</span></td></tr>
                <tr><td>Operating agreement</td><td className="n"><span className="tag t-pass">Final</span></td></tr>
                <tr><td>Subscription (e-sign)</td><td className="n"><span className="tag t-watch">In progress</span></td></tr>
                <tr><td>Accreditation evidence</td><td className="n"><span className="tag t-watch">Collecting</span></td></tr>
                <tr><td>Form D / Blue-Sky</td><td className="n">Reminder set</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* SCORE */}
        <h2 className="sechead" id="sec-score">The composite score, decomposed</h2>
        <div className="grid deal-two-col" style={{ gridTemplateColumns: "auto 1fr", alignItems: "center" }}>
          <div className="card pad" style={{ textAlign: "center" }}><Ring v={s.composite} band={s.band} gated={s.gated} /></div>
          <div className="card pad">
            <div className="tablewrap">
              <table className="t">
                <thead><tr><th>Pillar</th><th className="n">Weight</th><th className="n">Score</th><th></th></tr></thead>
                <tbody>
                  <PillarRow name="Financial return" w={40} v={s.fin} />
                  <PillarRow name="Location durability" w={25} v={s.loc} />
                  <PillarRow name="Compliance & execution" w={20} v={s.ce} />
                  <PillarRow name="Deal / market quality" w={15} v={s.dq} />
                  <tr className="tot"><td>Weighted composite</td><td className="n">100%</td><td className="n">{s.gated ? "—" : s.composite}</td><td>{s.band}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* REPORT */}
        <h2 className="sechead" id="sec-report">Report</h2>
        <Pe>Generate a branded package for a bank or investor. Use your browser's Print → Save as PDF to export it.</Pe>
        <div className="card pad">
          <div className="noprint" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn btn-p btn-sm" onClick={() => window.print()}>🖨 Print / Save investor memo (PDF)</button>
          </div>
          <ReportActions deal={deal} onChange={onChange} />
          <div style={{ marginTop: 16, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <div className="fr" style={{ fontSize: 20, fontWeight: 600, color: "var(--forest)" }}>Investment Memorandum — {deal.name}</div>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>{deal.addr || deal.market} · prepared for LP review</div>
              </div>
              <span className={"tag t-" + (s.cls === "pass" ? "pass" : s.cls === "watch" ? "watch" : "fail")}>{s.band}{s.gated ? "" : " · " + s.composite}</span>
            </div>
            <div className="grid g4" style={{ marginTop: 14 }}>
              <Kpi label="All-in" value={money0(m.allIn)} />
              <Kpi label="Stabilized value" value={money0(m.stabVal)} />
              <Kpi label="DSCR" value={x2(m.dscr)} />
              <Kpi label="Proj. LP IRR" value={m.lpIRR ? pct(m.lpIRR) : "—"} />
            </div>
            <div className="note" style={{ marginTop: 12 }}>
              Decision-support, not investment advice. Illustrative figures. Bridge-to-permanent; takeout to agency at stabilization.
              {deal.oz ? " OZ benefit time-limited to the current designation." : ""}
              {deal.flood !== "X" ? " Flood-zone insurance modeled as a first-order risk." : ""}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ---- small presentational helpers ----
function Kpi({ label, value, sub, good }: { label: React.ReactNode; value: string; sub?: string; good?: boolean }) {
  return (
    <div className="card kpi">
      <div className="kl">{label}</div>
      <div className="kv">{value}</div>
      {sub && <div className={"ks" + (good === true ? " good" : good === false ? " bad" : "")}>{sub}</div>}
    </div>
  );
}
function Row({ l, v, tot }: { l: React.ReactNode; v: string; tot?: boolean }) {
  return (
    <tr className={tot ? "tot" : undefined}>
      <td>{l}</td>
      <td className="n">{v}</td>
    </tr>
  );
}
function MetricRow({ l, v, t, ok }: { l: React.ReactNode; v: string; t: string; ok: boolean }) {
  return (
    <tr>
      <td>{l}</td>
      <td className="n">{v}</td>
      <td className="n">{t}</td>
      <td><span className={"tag " + (ok ? "t-pass" : "t-watch")}>{ok ? "OK" : "Tight"}</span></td>
    </tr>
  );
}
function PillarRow({ name, w, v }: { name: string; w: number; v: number }) {
  return (
    <tr>
      <td>{name}</td>
      <td className="n">{w}%</td>
      <td className="n">{v}</td>
      <td><div className="bar" style={{ width: 120 }}><span style={{ width: v + "%" }} /></div></td>
    </tr>
  );
}
function WfStep({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <div className="wf" style={{ display: "flex", gap: 11, alignItems: "flex-start", padding: "9px 0", borderBottom: "1px dashed var(--line)" }}>
      <div style={{ width: 22, height: 22, borderRadius: "50%", background: "var(--forest)", color: "var(--cream)", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{n}</div>
      <div style={{ fontSize: 13 }}>{children}</div>
    </div>
  );
}
function LpRow({ name, amt, mult }: { name: string; amt: number; mult: number }) {
  return (
    <tr>
      <td>{name}</td>
      <td className="n">{money0(amt)}</td>
      <td className="n">{money0(amt * mult)}</td>
    </tr>
  );
}
// ---- Eligibility & designations ----
const STATUS_META: Record<TriState, { cls: string; label: string }> = {
  yes: { cls: "t-pass", label: "In zone" },
  no: { cls: "t-fail", label: "Not in zone" },
  unknown: { cls: "t-watch", label: "Unverified" },
};

function overlayStatus(deal: Deal, o: Overlay): TriState {
  return o.key === "oz" ? (deal.oz ? "yes" : "unknown") : o.status;
}

function EligibilityPanel({ deal, onChange }: { deal: Deal; onChange: (patch: Partial<Deal>) => void }) {
  const elig = eligibility(deal, calc(deal));
  const [look, setLook] = useState<{ state: "idle" | "loading" | "done" | "error"; msg?: string }>({ state: "idle" });

  async function resolve() {
    if (!deal.addr.trim()) {
      setLook({ state: "error", msg: "Add a street address in Inputs first." });
      return;
    }
    setLook({ state: "loading" });
    try {
      const r = await resolveTract(deal.addr);
      if (!r) {
        setLook({ state: "error", msg: "No match — check the address, or enter the tract by hand." });
        return;
      }
      onChange({ censusTract: r.geoid });
      setLook({ state: "done", msg: `${r.matchedAddress} → tract ${r.geoid}` });
    } catch {
      setLook({ state: "error", msg: "Browser couldn't reach the Census geocoder — enter the tract manually or use the official maps below." });
    }
  }

  // key -> how to write the status back to the deal
  const setStatus = (o: Overlay, v: string) => {
    if (o.key === "oz") onChange({ oz: v === "yes" });
    else if (o.key === "qct") onChange({ qct: v as TriState });
    else if (o.key === "dda") onChange({ dda: v as TriState });
    else if (o.key === "usda") onChange({ usdaRural: v as TriState });
  };

  return (
    <>
      <Pe>
        These are per-address federal designations — you verify each by dropping the property on an official map, not by a one-time approval.{" "}
        <b>Florida's old Enterprise Zone program expired in 2015</b>; the overlays below are what actually move affordable-housing economics today.
      </Pe>

      <div className="card pad" style={{ marginBottom: 12 }}>
        <div className="eyebrow">Census tract — the key every designation is looked up by</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
          <button className="btn btn-a btn-sm noprint" onClick={resolve} disabled={look.state === "loading"}>
            {look.state === "loading" ? "Resolving…" : "📍 Resolve from address"}
          </button>
          <div className="mono" style={{ fontSize: 13, color: "var(--forest)" }}>
            {deal.censusTract ? `Tract ${deal.censusTract}` : "No tract resolved yet"}
          </div>
        </div>
        {look.msg && (
          <div className={"flag " + (look.state === "error" ? "warn" : "ok")} style={{ marginTop: 8 }}>
            <span>{look.state === "error" ? "◐" : "✓"}</span><div>{look.msg}</div>
          </div>
        )}
        <div className="note">Uses the free US Census Geocoder. In cloud mode this becomes an automatic feed that also returns QCT/DDA/OZ status (Phase 2).</div>
      </div>

      <div className="grid g2">
        {elig.overlays.map((o) => {
          const st = overlayStatus(deal, o);
          const meta = STATUS_META[st];
          return (
            <div className="card pad" key={o.key}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--forest)" }}>
                  {o.full} <span className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>{o.label}</span>
                </div>
                <span className={"tag " + meta.cls}>{meta.label}</span>
              </div>
              <div style={{ fontSize: 12.5, color: "#37413c", marginTop: 6 }}>{o.what}</div>
              <div style={{ fontSize: 12.5, color: "#37413c", marginTop: 6 }}><b>Why it matters:</b> {o.why}</div>
              {o.impact && st === "yes" && (
                <div className="flag ok" style={{ marginTop: 8 }}>
                  <span>✓</span><div>{o.impactLabel}: <b>{money0(o.impact)}</b> <span style={{ color: "var(--muted)" }}>(illustrative — only if LIHTC is layered on)</span></div>
                </div>
              )}
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
                <select className="noprint" value={st} onChange={(e) => setStatus(o, e.target.value)} style={{ padding: "5px 8px", border: "1px solid var(--line)", borderRadius: 7, fontSize: 12.5, background: "var(--white)" }}>
                  <option value="unknown">Unverified</option>
                  <option value="yes">In zone</option>
                  <option value="no">Not in zone</option>
                </select>
                <a className="btn btn-g btn-sm noprint" href={o.verifyUrl} target="_blank" rel="noopener noreferrer">Verify on {o.source} ↗</a>
              </div>
            </div>
          );
        })}
      </div>

      {elig.boostEligible && (
        <div className="flag ok" style={{ marginTop: 12 }}>
          <span>✓</span>
          <div>
            This tract qualifies for the <b>30% LIHTC basis boost</b> (QCT or DDA). If you layer tax credits, that's roughly <b>{money0(elig.basisBoost)}</b> of extra eligible basis on this deal's rehab + soft costs. <span style={{ color: "var(--muted)" }}>Illustrative — confirm eligible basis with your tax-credit consultant.</span>
          </div>
        </div>
      )}
      {elig.ruralPath && (
        <div className="flag ok" style={{ marginTop: 8 }}>
          <span>✓</span><div>Address is <b>USDA rural-eligible</b> — USDA Rural Development 515/538 multifamily financing is on the table alongside the agency takeout. The same rural map governs USDA Community Facilities (rural healthcare) loans.</div>
        </div>
      )}
    </>
  );
}

// ---- Data sources (third-party wiring) ----
const STATUS_BADGE: Record<ProviderStatus, { cls: string; label: string }> = {
  live: { cls: "t-pass", label: "Live now" },
  "needs-key": { cls: "t-watch", label: "Add free key" },
  edge: { cls: "t-watch", label: "Cloud · Phase 2" },
  paid: { cls: "t-fail", label: "Paid · Phase 2" },
};
const COST_LABEL: Record<string, string> = {
  free: "Free",
  "free-key": "Free (key)",
  token: "Free (token)",
  paid: "Paid",
};

const changeValue = (field: string, v: unknown): string => {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (field === "taxes" || field === "safmr2" || field === "openLiens") return money0(Number(v));
  return String(v);
};

function DataSourcesPanel({ deal, onChange }: { deal: Deal; onChange: (patch: Partial<Deal>) => void }) {
  const [intel, setIntel] = useState<AreaIntel | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "warn"; text: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [enr, setEnr] = useState<DealEnrichment | null>(null);
  const [enriching, setEnriching] = useState(false);

  const acs = PROVIDERS.find((p) => p.id === "census-acs")!;
  const acsLive = providerStatus(acs) === "live";

  async function autoFill() {
    if (!deal.addr.trim()) {
      setMsg({ kind: "warn", text: "Add a street address in Inputs first." });
      return;
    }
    setEnriching(true);
    setEnr(null);
    try {
      setEnr(await enrichDeal(deal));
    } finally {
      setEnriching(false);
    }
  }

  async function pullArea() {
    if (!deal.censusTract) {
      setMsg({ kind: "warn", text: "Resolve the census tract first (Eligibility tab)." });
      return;
    }
    setLoading(true);
    setMsg(null);
    try {
      const r = await fetchAreaIntel(deal.censusTract);
      if (!r) {
        setMsg({ kind: "warn", text: "No data returned — check the tract or the Census key." });
      } else {
        setIntel(r);
        setMsg({ kind: "ok", text: `Pulled from ${r.source}.` });
      }
    } catch {
      setMsg({ kind: "warn", text: "Census request failed — a free VITE_CENSUS_KEY is needed (see .env.example)." });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Pe>
        Everything Beacon can know about a property flows through one uniform "data provider" layer. Most of the foundation — assessments, demographics, income, rent ceilings, flood — is <b>free government data</b>; you pay only to buy <em>coverage &amp; convenience</em> (mortgages/liens, rent comps, nationwide parcels). Each source is cited to its origin and pull date.
      </Pe>

      {/* AUTO-FILL — the "paste an address, get a filled deal file" flow */}
      <div className="card pad" style={{ marginBottom: 12, borderLeft: "4px solid var(--brass)" }}>
        <div className="eyebrow">One-click enrich — pull every source for this address</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
          <button className="btn btn-p btn-sm noprint" onClick={autoFill} disabled={enriching}>
            {enriching ? "Gathering…" : "✨ Auto-fill from address"}
          </button>
          <div style={{ fontSize: 12.5, color: "var(--muted)" }}>{deal.addr || "no address set"}</div>
        </div>
        {enr && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12.5, marginBottom: 8 }}>
              {enr.matchedAddress ? <>Matched <b>{enr.matchedAddress}</b>{enr.tract ? <> · tract <span className="mono">{enr.tract}</span></> : null}</> : "No address match."}
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
              {enr.sources.map((s) => (
                <span key={s.source} className={"tag " + (s.ok ? "t-pass" : "t-watch")} title={s.note}>
                  {s.ok ? "✓" : "○"} {s.source}
                </span>
              ))}
            </div>
            {enr.changes.length > 0 ? (
              <>
                <div className="tablewrap">
                  <table className="t">
                    <thead><tr><th>Field</th><th>Now</th><th>Proposed</th><th>Source</th></tr></thead>
                    <tbody>
                      {enr.changes.map((c) => (
                        <tr key={String(c.field)}>
                          <td>{c.label}{c.estimate && <span className="tag t-watch" style={{ marginLeft: 6 }}>est.</span>}</td>
                          <td style={{ color: "var(--muted)" }}>{changeValue(String(c.field), c.from)}</td>
                          <td style={{ fontWeight: 600, color: "var(--forest)" }}>{changeValue(String(c.field), c.to)}</td>
                          <td style={{ fontSize: 11.5, color: "var(--muted)" }}>{c.source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="btn btn-a btn-sm noprint" onClick={() => { onChange(enr.patch); setEnr(null); setMsg({ kind: "ok", text: `Applied ${enr.changes.length} field(s) — everything recomputed.` }); }}>
                    Apply {enr.changes.length} change{enr.changes.length > 1 ? "s" : ""}
                  </button>
                  <button className="btn btn-g btn-sm noprint" onClick={() => setEnr(null)}>Dismiss</button>
                </div>
              </>
            ) : (
              <div className="note">No new fields to propose — either everything already matches, or the address-specific sources (parcel, FMR) need the cloud Edge Function.</div>
            )}
            {enr.notes.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div className="eyebrow">Market read</div>
                {enr.notes.map((n, idx) => (
                  <div key={idx} className="flag ok" style={{ marginTop: 6 }}>
                    <span>◆</span><div dangerouslySetInnerHTML={{ __html: n.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>") }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="note">Locally this pulls the keyless/keyed browser sources; the FL parcel roll and HUD FMR resolve in the Supabase Edge Function <span className="mono">enrich</span> (cloud mode), which caches every pull into <span className="mono">data_cache</span>.</div>
      </div>

      <div className="card pad" style={{ marginBottom: 12 }}>
        <div className="tablewrap">
          <table className="t">
            <thead><tr><th>Source</th><th>What it fills</th><th>Cost</th><th>Status</th></tr></thead>
            <tbody>
              {PROVIDERS.map((p) => {
                const st = providerStatus(p);
                const b = STATUS_BADGE[st];
                return (
                  <tr key={p.id}>
                    <td>
                      <a href={p.url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>{p.label} ↗</a>
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>{p.source}</div>
                      {p.fairHousingNote && <div style={{ fontSize: 10.5, color: "var(--brass-deep)", marginTop: 2 }}>⚠ {p.fairHousingNote}</div>}
                    </td>
                    <td style={{ fontSize: 12, color: "#37413c" }}>{p.fills.join(", ")}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{COST_LABEL[p.cost]}</td>
                    <td><span className={"tag " + b.cls}>{b.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="note">"Live now" = works in this browser today. "Add free key" = paste a free API key into <span className="mono">.env</span>. "Cloud · Phase 2" = runs in a Supabase Edge Function (holds tokens; caches heavy/slow sources like the 10.8M-parcel FL roll).</div>
      </div>

      <div className="card pad">
        <div className="eyebrow">Live pull — area demographics &amp; income (Census ACS)</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
          <button className="btn btn-a btn-sm noprint" onClick={pullArea} disabled={loading || !acsLive}>
            {loading ? "Pulling…" : "📊 Pull area data for this tract"}
          </button>
          <div className="mono" style={{ fontSize: 12.5, color: "var(--forest)" }}>
            {deal.censusTract ? `Tract ${deal.censusTract}` : "No tract yet"}
          </div>
          {!acsLive && <span className="tag t-watch">Add VITE_CENSUS_KEY to enable</span>}
        </div>
        {msg && (
          <div className={"flag " + (msg.kind === "ok" ? "ok" : "warn")} style={{ marginTop: 8 }}>
            <span>{msg.kind === "ok" ? "✓" : "◐"}</span><div>{msg.text}</div>
          </div>
        )}
        {intel && (
          <>
            <div className="grid g3" style={{ marginTop: 10 }}>
              <Kpi label="Median HH income" value={intel.medianIncome ? money0(intel.medianIncome) : "—"} sub={intel.source} />
              <Kpi label="Renter share" value={intel.renterSharePct != null ? intel.renterSharePct + "%" : "—"} sub={`${money0(intel.renterOcc ?? 0)} of ${money0((intel.ownerOcc ?? 0) + (intel.renterOcc ?? 0))} occ.`} />
              <Kpi label="Median gross rent" value={intel.medianGrossRent ? money0(intel.medianGrossRent) : "—"} sub="tract-wide, all beds" />
            </div>
            {intel.renterSharePct != null && (
              <div style={{ marginTop: 10 }}>
                <button className="btn btn-g btn-sm noprint" onClick={() => onChange({ renterRising: intel.renterSharePct! >= 50 })}>
                  Apply "renter-majority" signal → {intel.renterSharePct >= 50 ? "renter share rising: Yes" : "No"}
                </button>
              </div>
            )}
          </>
        )}
        <div className="note">A free Census key unlocks this in dev; in cloud mode the Edge Function holds it and also fetches assessments (FL parcel), FMR, flood, and paid mortgage/lien/comps data — caching each into <span className="mono">data_cache</span>.</div>
      </div>
    </>
  );
}

// ---- Report actions: AI analysis (Claude) + email memo (Resend), cloud-only ----
function ReportActions({ deal, onChange }: { deal: Deal; onChange: (patch: Partial<Deal>) => void }) {
  const cloud = isCloud();
  const [ai, setAi] = useState<{ state: "idle" | "run" | "done" | "err"; text?: string }>({ state: "idle" });
  const [to, setTo] = useState("");
  const [mail, setMail] = useState<{ kind: "ok" | "warn"; text: string } | null>(null);
  const [sending, setSending] = useState(false);
  const [crm, setCrm] = useState<{ state: "idle" | "run" | "ok" | "warn"; text?: string }>({ state: "idle" });

  if (!hasFeature("ai")) return <div className="noprint" style={{ marginTop: 14 }}><Upsell feature="ai" /></div>;

  async function pushCrm() {
    setCrm({ state: "run" });
    const r = await syncDealToGhl(deal);
    if (!r.configured) { setCrm({ state: "warn", text: "GoHighLevel isn't connected yet — add the token in Setup → CRM sync." }); return; }
    if (r.error) { setCrm({ state: "warn", text: r.error }); return; }
    onChange({ ghlContactId: r.contactId, ghlOpportunityId: r.opportunityId, ghlSyncedAt: new Date().toISOString() });
    setCrm({ state: "ok", text: "Pushed to GoHighLevel." });
  }

  async function runAnalysis() {
    setAi({ state: "run" });
    try {
      const r = await analyzeDeal(deal);
      setAi({ state: "done", text: r.text });
    } catch (e) {
      setAi({ state: "err", text: e instanceof Error ? e.message : String(e) });
    }
  }
  async function sendMemo() {
    setSending(true);
    setMail(null);
    try {
      await emailMemo(deal, to.trim());
      setMail({ kind: "ok", text: `Memo sent to ${to.trim()}.` });
      setTo("");
    } catch (e) {
      setMail({ kind: "warn", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="noprint" style={{ marginTop: 14, borderTop: "1px dashed var(--line)", paddingTop: 14 }}>
      {!cloud && (
        <div className="flag warn" style={{ marginBottom: 10 }}>
          <span>◐</span><div>AI analysis (Claude) and email (Resend) run in the cloud Edge Functions. Add Supabase keys to <span className="mono">.env</span> and deploy <span className="mono">analyze</span> / <span className="mono">notify</span> — see <span className="mono">docs/deploy.md</span>.</div>
        </div>
      )}

      {/* AI analysis */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button className="btn btn-a btn-sm" onClick={runAnalysis} disabled={!cloud || ai.state === "run"}>
          {ai.state === "run" ? "Analyzing…" : "✨ Analyze this deal with AI"}
        </button>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>Claude reads the numbers &amp; gates, returns a plain-English read.</span>
      </div>
      {ai.state === "done" && (
        <div className="pe" style={{ marginTop: 10 }}>
          <div className="l">◆ AI investment read</div>
          <p style={{ whiteSpace: "pre-wrap" }}>{ai.text}</p>
        </div>
      )}
      {ai.state === "err" && <div className="flag bad" style={{ marginTop: 8 }}><span>!</span><div>{ai.text}</div></div>}

      {/* Email memo */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
        <input
          type="email"
          placeholder="investor@email.com"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          disabled={!cloud}
          style={{ padding: "7px 10px", border: "1px solid var(--line)", borderRadius: 7, fontSize: 13, minWidth: 220, background: "var(--white)" }}
        />
        <button className="btn btn-g btn-sm" onClick={sendMemo} disabled={!cloud || sending || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to.trim())}>
          {sending ? "Sending…" : "✉ Email the memo"}
        </button>
      </div>
      {mail && (
        <div className={"flag " + (mail.kind === "ok" ? "ok" : "warn")} style={{ marginTop: 8 }}>
          <span>{mail.kind === "ok" ? "✓" : "◐"}</span><div>{mail.text}</div>
        </div>
      )}

      {/* CRM push (GoHighLevel — downstream mirror) */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
        <button className="btn btn-g btn-sm" onClick={pushCrm} disabled={!cloud || crm.state === "run"}>
          {crm.state === "run" ? "Pushing…" : "⇪ Push to CRM (GoHighLevel)"}
        </button>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>
          {deal.ghlSyncedAt ? `Last synced ${deal.ghlSyncedAt.slice(0, 10)}` : "One-way mirror — Beacon stays the source of truth."}
        </span>
      </div>
      {crm.state === "ok" && <div className="flag ok" style={{ marginTop: 8 }}><span>✓</span><div>{crm.text}</div></div>}
      {crm.state === "warn" && <div className="flag warn" style={{ marginTop: 8 }}><span>◐</span><div>{crm.text}</div></div>}
    </div>
  );
}

function GateCard({ letter, title, g }: { letter: string; title: string; g: Gate }) {
  const bg = g.s === "pass" ? "var(--pass)" : g.s === "cond" ? "var(--brass)" : "var(--fail)";
  return (
    <div className="card pad" style={{ display: "flex", gap: 13 }}>
      <div style={{ width: 34, height: 34, borderRadius: 8, background: bg, color: "#fff", display: "grid", placeItems: "center", fontFamily: "Fraunces", fontWeight: 600, flexShrink: 0 }}>{letter}</div>
      <div>
        <div style={{ fontSize: 14, color: "var(--forest)", fontWeight: 600 }}>
          {title} <span className={tagClass(g.s)}>{g.t}</span>
        </div>
        <div style={{ fontSize: 12.5, color: "#37413c", marginTop: 5 }}>{g.txt}</div>
      </div>
    </div>
  );
}
